/**
 * PayrollSummary.tsx - Incentive summary + payroll warning modal
 *
 * Phase 2: payroll-management.tsx component decomposition
 * FR-12: Extracted JSX section
 */

import React from 'react';
import { usePayrollRender } from './PayrollRenderContext.tsx';

export const PayrollSummary: React.FC = () => {
  const {
    budgetData,
    calculateNetSalaryWithAllowances,
    calculateRealTimeIndividualIncentive,
    employees,
    handlePayrollWarningCancel,
    handlePayrollWarningConfirm,
    monthlyTeamTargetSales,
    showPayrollWarningModal,
    teamActualSales,
    totalIncentive,
    totalTargetSales,
    user,
    userTeamName,
  } = usePayrollRender();

  return (
    <>
      {/* 인센티브 요약 섹션 */}
      <div className="bg-white p-2 sm:p-3 md:p-4 rounded-lg shadow-sm mb-2 sm:mb-3">
        <h2 className="text-base sm:text-lg font-medium mb-2 sm:mb-4">
          {user?.role === 'employee' && userTeamName ? `${userTeamName} 팀 인건비 및 인센티브 요약` : '최종 인건비 및 인센티브 요약'}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-4">
          {/* 최종 인건비 요약 - 왼쪽 */}
          <div className="bg-gray-50 p-4 rounded">
            <h3 className="text-md font-semibold mb-2">
              {user?.role === 'employee' && userTeamName ? `${userTeamName} 팀 인건비 구성 (월별)` : '전체 인건비 구성 (월별)'}
            </h3>
            {(() => {
              const filteredEmployees = employees.filter(emp => user?.role !== 'employee' || emp.team === userTeamName);
              const filteredSalary = filteredEmployees.reduce((sum, emp) => sum + emp.salary, 0);
              const filteredIncentive = filteredEmployees.reduce((sum, emp) => sum + calculateRealTimeIndividualIncentive(emp), 0);
              const filteredRetirement = filteredEmployees.reduce((sum, emp) => sum + (emp.retirement || 0), 0);
              const filteredAllowances = filteredEmployees.reduce((sum, emp) => 
                sum + (emp.meal || 0) + (emp.housing || 0) + (emp.overtime || 0) + (emp.individual || 0) + (emp.other || 0), 0);
              
              return (
                <>
                  <div className="flex justify-between items-center mb-1"><span className="text-sm">기본급여 총액:</span><span className="font-semibold tabular-nums">{filteredSalary.toLocaleString()}원</span></div>
                  <div className="flex justify-between items-center mb-1"><span className="text-sm">인센티브 총액:</span><span className="font-semibold tabular-nums">{filteredIncentive.toLocaleString()}원</span></div>
                  <div className="flex justify-between items-center mb-1"><span className="text-sm">퇴직급여 총액 (월적립):</span><span className="font-semibold tabular-nums">{filteredRetirement.toLocaleString()}원</span></div>
                  <div className="flex justify-between items-center mb-1"><span className="text-sm">기타수당 총액 (식대 등):</span><span className="font-semibold tabular-nums">{filteredAllowances.toLocaleString()}원</span></div>
                  <hr className="my-2"/>
                  <div className="flex justify-between items-center mb-1 text-purple-700"><span className="text-sm font-medium">총 인건비 (인센티브 제외, 세전):</span><span className="font-semibold tabular-nums">{(filteredSalary + filteredRetirement + filteredAllowances).toLocaleString()}원</span></div>
                  <div className="flex justify-between items-center font-bold text-blue-700"><span className="text-sm">총 인건비 (세전, 회사부담):</span><span>{(filteredSalary + filteredIncentive + filteredRetirement + filteredAllowances).toLocaleString()}원</span></div>
                  <div className="flex justify-between items-center text-sm mt-1"><span className="text-gray-600">└ 직원 실지급액 총계(세후):</span><span className="text-gray-600">{filteredEmployees.reduce((sum, emp) => sum + calculateNetSalaryWithAllowances(emp), 0).toLocaleString()}원</span></div>
                </>
              );
            })()}
          </div>
          {/* 인센티브 요약 - 오른쪽 */}
          <div className="bg-gray-50 p-4 rounded">
            <h3 className="text-md font-semibold mb-2">인센티브 배분 (실제매출 기준)</h3>
            {(() => {
              // 직원 역할인 경우 본인 팀의 데이터만 표시
              if (user?.role === 'employee' && userTeamName) {
                const teamTargetSales = monthlyTeamTargetSales[userTeamName] || 0;
                const teamActualSalesAmount = teamActualSales[userTeamName] || 0;
                const teamIncentive = employees
                  .filter(emp => emp.team === userTeamName)
                  .reduce((sum, emp) => sum + calculateRealTimeIndividualIncentive(emp), 0);
                
                return (
                  <>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">{userTeamName} 팀 목표매출:</span>
                      <span className="font-semibold tabular-nums">{teamTargetSales.toLocaleString()}원</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">{userTeamName} 팀 실제매출:</span>
                      <span className="font-semibold tabular-nums">{teamActualSalesAmount.toLocaleString()}원</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">{userTeamName} 팀 초과달성액:</span>
                      <span className="font-semibold tabular-nums text-green-600">
                        {Math.max(0, teamActualSalesAmount - teamTargetSales).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">{userTeamName} 팀 지급 인센티브:</span>
                      <span className="font-semibold tabular-nums text-gray-900">{teamIncentive.toLocaleString()}원</span>
                    </div>
                  </>
                );
              } else {
                // 전체 데이터 표시
                return (
                  <>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">전체 목표매출:</span>
                      <span className="font-semibold tabular-nums">{totalTargetSales.toLocaleString()}원</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">전체 실제매출:</span>
                      <span className="font-semibold tabular-nums">{budgetData.actualSales.toLocaleString()}원</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">전체 초과달성액:</span>
                      <span className="font-semibold tabular-nums text-green-600">
                        {Math.max(0, budgetData.actualSales - totalTargetSales).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">총 지급 인센티브:</span>
                      <span className="font-semibold tabular-nums text-gray-900">{totalIncentive.toLocaleString()}원</span>
                    </div>
                  </>
                );
              }
            })()}
          </div>
        </div>
      </div>

      {/* 급여 관리 확인 버튼 경고 모달 */}
      {showPayrollWarningModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="payroll-warning-title"
          onKeyDown={(e) => { if (e.key === 'Escape') handlePayrollWarningCancel(); }}
        >
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 id="payroll-warning-title" className="text-lg font-medium text-gray-900">
                  주의사항
                </h3>
              </div>
            </div>
            <div className="mb-6">
              <p className="text-sm text-gray-600">
                대략적인 수령액 확인용으로만 사용하시고, 정확한 금액은 실제 급여명세서를 확인바랍니다.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handlePayrollWarningCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
              >
                취소
              </button>
              <button
                autoFocus
                onClick={handlePayrollWarningConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
